import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api';
import type {
  AddOrderV3Request,
  CreateDigitalBillAndMoveOrderLinesDto,
  ErrorMessage,
  MoveOrderLinesToDigitalBillRequest,
  MoveToExistingDigitalBillDto,
  PayByAmountDigitalBillDto,
  SplitByAmountSuccessResponse,
  SubmittedDigitalBillDto,
} from '../../generated-api';
import type { UnsettledDigitalBillDto } from '../types/digitalBill';
import type {
  InhouseDigitalBillDetailsDto,
  InhouseDigitalBillOrderLineDetailsDto,
  PrintQrCodeDto,
  VerifyBookingDto,
} from '@ordr-as/ordr-service-types/epos';
import {
  InhouseDigitalBillOrderLineDetailsDtoCategory,
  getGetBookingDetailDtoQueryKey,
  getGetUnsettledBookingsV2QueryKey,
  useCreateBooking,
  useDeleteDigitalBill,
  useDiscard,
  useGetBookingDetailDto,
  useGetQRCodeV2,
  useGetSettledBookingsV2,
  useGetUnsettledBookingsV2,
  useMerge,
  usePrintQrCode,
  useSplit,
  useUpdateBillLimit as useUpdateBillLimitGenerated,
  useVerifyBookingWithProfileId,
} from '@ordr-as/ordr-service-types/epos';
import { billDetailsRefetchInterval, isBillLimitExceededError } from '../utils/digitalBillUtils';
import { parseError } from '../utils/error-handler';
import { useToast } from '@ordr-as/umami-react/toast';
import { useIntl } from 'react-intl';
import { isValidLocationId } from '../utils/locationId';
import { useHub } from '../context/HubProvider';
import { useLocation } from '../context/LocationContext';
import useDeviceId from './useDeviceId';

export function useQueryUnsettledDigitalBills(locationId: string) {
  const { queryKey: _queryKey, ...result } = useGetUnsettledBookingsV2(locationId, {
    query: {
      enabled: isValidLocationId(locationId),
      refetchInterval: 120000,
      refetchOnMount: true,
    },
  });
  return result;
}

export function useQuerySettledDigitalBills(locationId: string, isDigitalBillEnabled: boolean) {
  const { queryKey: _queryKey, ...result } = useGetSettledBookingsV2(locationId, {
    query: {
      enabled: isValidLocationId(locationId) && isDigitalBillEnabled,
      refetchInterval: 120000,
      refetchOnMount: true,
    },
  });
  return result;
}

const toReadableCategoryName = (category: InhouseDigitalBillOrderLineDetailsDtoCategory | undefined) => {
  switch (category) {
    case InhouseDigitalBillOrderLineDetailsDtoCategory.TIP: {
      return 'Tip';
    }
    case InhouseDigitalBillOrderLineDetailsDtoCategory.DELIVERY_FEE: {
      return 'Delivery Fee';
    }
    case InhouseDigitalBillOrderLineDetailsDtoCategory.SERVICE_CHARGE: {
      return 'Service Charge';
    }
    default: {
      return 'Regular';
    }
  }
};

const mapDeliveryInformation = (
  deliveryInformation: InhouseDigitalBillDetailsDto['orders'][number]['deliveryInformation']
) => {
  if (!deliveryInformation?.primaryReference) {
    return undefined;
  }

  const { primaryReference, secondaryReference, tertiaryReference, deliveryMethod } = deliveryInformation;

  if (!deliveryMethod) {
    return {
      deliveryMethod: undefined,
      primaryReference,
      secondaryReference,
      tertiaryReference,
    };
  }

  return {
    deliveryMethod: {
      deliveryType: deliveryMethod.deliveryType,
      feeAmount: deliveryMethod.feeAmount,
      id: deliveryMethod.id,
      serviceChargePercentage: deliveryMethod.serviceChargePercentage,
    },
    primaryReference,
    secondaryReference,
    tertiaryReference,
  };
};

function mapDigitalBillDetails(dto: InhouseDigitalBillDetailsDto): InhouseDigitalBillDetailsDto {
  const mapOrderLine = (line: InhouseDigitalBillOrderLineDetailsDto): InhouseDigitalBillOrderLineDetailsDto => ({
    ...line,
    components: (line.components as InhouseDigitalBillOrderLineDetailsDto[] | undefined)?.map(mapOrderLine),
    name: line.name ?? toReadableCategoryName(line.category),
    options: line.options ?? [],
  });

  const mappedOrders = dto.orders
    .filter((order) => order.orderNumber != null)
    .map((order) => ({
      ...order,
      bookingDoneBy: order.bookingDoneBy,
      deliveryInformation: mapDeliveryInformation(order.deliveryInformation),
      orderLines: order.orderLines.map(mapOrderLine),
    }));

  return {
    ...dto,
    mainContactDetails: dto.mainContactDetails && {
      name: dto.mainContactDetails.name ?? '',
      phone: dto.mainContactDetails.phone,
      phoneAvailable: dto.mainContactDetails.phoneAvailable ?? false,
      userType: dto.mainContactDetails.userType ?? '',
    },
    orders: mappedOrders,
  } as InhouseDigitalBillDetailsDto;
}

export function useQueryDigitalBillDetails(isDigitalBillEnabled: boolean, locationId?: string, reference?: string) {
  const { queryKey: _queryKey, ...result } = useGetBookingDetailDto(locationId ?? '', reference ?? '', {
    query: {
      enabled: isValidLocationId(locationId) && !!reference && isDigitalBillEnabled,
      // query.state.data is the raw dto (select has not been applied)
      refetchInterval: (query) => billDetailsRefetchInterval(query.state.data),
      refetchOnMount: true,
      select: mapDigitalBillDetails,
    },
  });
  return result;
}

export function useCreateDigitalBill(locationId: string) {
  const queryClient = useQueryClient();
  const hub = useHub();

  return useCreateBooking({
    mutation: {
      mutationKey: ['create-digital-bill', locationId],
      onSuccess: (data) => {
        queryClient.setQueryData<UnsettledDigitalBillDto[]>(
          getGetUnsettledBookingsV2QueryKey(locationId),
          (digitalBills) => [data, ...(digitalBills || [])]
        );
        hub.broadcast('digitalBill.created', {
          locationId,
          reference: data.reference,
        });
      },
    },
  });
}

export function useMutateDeleteDigitalBill(locationId: string, reference: string) {
  const queryClient = useQueryClient();
  const hub = useHub();
  const deviceId = useDeviceId();

  return useDeleteDigitalBill({
    mutation: {
      mutationKey: ['delete-digital-bill'],
      onSuccess: () => {
        queryClient.setQueryData<UnsettledDigitalBillDto[]>(
          getGetUnsettledBookingsV2QueryKey(locationId),
          (digitalBills) => digitalBills?.filter((digitalBill) => digitalBill.reference !== reference) || []
        );
        hub.broadcast('digitalBill.deleted', {
          deviceId,
          locationId,
          reference,
        });
      },
    },
  });
}

export const useMutateAddOrderToDigitalBill = () => {
  const apiClient = useApiClient();
  const hub = useHub();

  return useMutation<SubmittedDigitalBillDto, ErrorMessage, AddOrderV3Request>({
    mutationFn: async (addOrderRequest: AddOrderV3Request) =>
      apiClient.SubmitCartControllerApi.addOrderV3(addOrderRequest).catch(parseError),
    mutationKey: ['add-to-bill'],
    onSuccess: (data) => {
      hub.broadcast('digitalBill.updated', {
        reference: data.reference,
        locationId: data.cartDto!.locationId,
      });
    },
    // Bill-limit rejections are handled with a toast at the call sites; everything
    // else keeps throwing into the local error boundary
    throwOnError: (error) => !isBillLimitExceededError(error),
  });
};

export const useMutateInitiateSplitPaymentByAmount = (locationId: string, reference: string) => {
  const apiClient = useApiClient();

  const postPayment = async (payByAmountDigitalBillDto: PayByAmountDigitalBillDto) =>
    apiClient.SplitPaymentControllerApi.initiateSplitPayment({
      locationId,
      payByAmountDigitalBillDto,
      reference,
    }).catch(parseError);

  return useMutation<SplitByAmountSuccessResponse, ErrorMessage, PayByAmountDigitalBillDto>({
    mutationFn: postPayment,
    mutationKey: ['split-payment-by-amount'],
    throwOnError: true,
  });
};

const createObjectUrl = (blob: Blob) => URL.createObjectURL(blob);

export const useGetDigitalBillInviteQrCode = (reference: string) => {
  const { queryKey: _queryKey, ...result } = useGetQRCodeV2(reference, {
    query: {
      select: createObjectUrl,
      retry: false,
      // Prevent caching blob URLs - they should be recreated on each mount
      staleTime: 0,
      gcTime: 0,
    },
    request: { responseType: 'blob' },
  });
  return result;
};

export const useMutateInviteGuestToDigitalBillViaSms = (reference: string) => {
  const { location } = useLocation();

  const { mutate, mutateAsync, ...rest } = useVerifyBookingWithProfileId({
    mutation: {
      mutationKey: ['guest-invite-sms-code', reference],
    },
  });

  return {
    ...rest,
    mutate: (data: VerifyBookingDto, options?: Parameters<typeof mutate>[1]) =>
      mutate({ data, locationId: location.id, reference }, options),
    mutateAsync: (data: VerifyBookingDto, options?: Parameters<typeof mutateAsync>[1]) =>
      mutateAsync({ data, locationId: location.id, reference }, options),
  };
};

export const useMutatePrintDigitalBillInviteQrCode = (reference: string) => {
  const { location } = useLocation();
  const toasts = useToast();
  const intl = useIntl();

  const { mutate, mutateAsync, ...rest } = usePrintQrCode({
    mutation: {
      onError: () => {
        if (!toasts.exists(reference)) {
          toasts.pushError({
            id: reference,
            message: <span>{intl.formatMessage({ id: 'digitalBill.printQR.print.error' })}</span>,
          });

          setTimeout(() => {
            toasts.remove(reference);
          }, 1000);
        }
      },
      onSuccess: () => {
        if (!toasts.exists(reference)) {
          toasts.pushSuccess({
            id: reference,
            message: <span>{intl.formatMessage({ id: 'digitalBill.printQR.print.success' })}</span>,
          });
        }
      },
    },
  });

  return {
    ...rest,
    mutate: (data: PrintQrCodeDto, options?: Parameters<typeof mutate>[1]) =>
      mutate({ data, locationId: location.id, reference }, options),
    mutateAsync: (data: PrintQrCodeDto, options?: Parameters<typeof mutateAsync>[1]) =>
      mutateAsync({ data, locationId: location.id, reference }, options),
  };
};

export const useMoveToExistingBill = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const moveOrderLines = async (request: MoveOrderLinesToDigitalBillRequest) =>
    apiClient.MoveOrderLinesControllerApi.moveOrderLinesToDigitalBill(request, {
      headers: { 'Content-type': 'application/json' },
    }).catch(parseError);

  return useMutation<MoveToExistingDigitalBillDto, ErrorMessage, MoveOrderLinesToDigitalBillRequest>({
    mutationFn: moveOrderLines,
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: getGetUnsettledBookingsV2QueryKey(variables.locationId),
      });
      queryClient.invalidateQueries({
        queryKey: getGetBookingDetailDtoQueryKey(variables.locationId, variables.fromReference),
      });
      queryClient.invalidateQueries({
        queryKey: getGetBookingDetailDtoQueryKey(variables.locationId, variables.toReference),
      });
    },
  });
};

export const MoveToNewBill = (locationId: string, reference: string) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const postPayment = async (createDigitalBillAndMoveOrderLinesDto: CreateDigitalBillAndMoveOrderLinesDto) =>
    apiClient.MoveOrderLinesControllerApi.moveOrderLinesToNewDigitalBill(
      { createDigitalBillAndMoveOrderLinesDto, locationId, reference },
      {
        headers: { 'Content-type': 'application/json' },
      }
    ).catch(parseError);

  return useMutation<MoveToExistingDigitalBillDto, ErrorMessage, CreateDigitalBillAndMoveOrderLinesDto>({
    mutationFn: postPayment,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getGetUnsettledBookingsV2QueryKey(locationId),
      });
      queryClient.invalidateQueries({
        queryKey: getGetBookingDetailDtoQueryKey(locationId, reference),
      });
    },
  });
};

export const useDiscardItems = (locationId: string, reference: string) => {
  const queryClient = useQueryClient();

  return useDiscard({
    mutation: {
      mutationKey: ['discard-items', locationId, reference],
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: getGetUnsettledBookingsV2QueryKey(locationId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetBookingDetailDtoQueryKey(locationId, reference),
        });
      },
    },
  });
};

export const useSplitOrderLine = (locationId: string, reference: string) => {
  const queryClient = useQueryClient();

  return useSplit({
    mutation: {
      mutationKey: ['split-order-line', locationId, reference],
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: getGetUnsettledBookingsV2QueryKey(locationId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetBookingDetailDtoQueryKey(locationId, reference),
        });
      },
    },
  });
};

/**
 * `locationId` here is the public string id, used only for cache keys and the hub
 * broadcast. The request path takes the internal numeric id, so callers pass
 * `location.id` in the mutation payload — the two ids are not interchangeable.
 */
export const useUpdateBillLimit = (locationId: string, reference: string) => {
  const queryClient = useQueryClient();
  const hub = useHub();

  return useUpdateBillLimitGenerated({
    mutation: {
      // Handled in the dialog; must not reach the full-screen mutation error boundary
      meta: { skipGlobalErrorBoundary: true },
      mutationKey: ['update-bill-limit', locationId, reference],
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: getGetBookingDetailDtoQueryKey(locationId, reference),
        });
        // The bill list row shows the limit too
        queryClient.invalidateQueries({
          queryKey: getGetUnsettledBookingsV2QueryKey(locationId),
        });
        hub.broadcast('digitalBill.updated', {
          locationId,
          reference,
        });
      },
    },
  });
};

export const useMergeOrderLine = (locationId: string, reference: string) => {
  const queryClient = useQueryClient();

  return useMerge({
    mutation: {
      mutationKey: ['merge-order-line', locationId, reference],
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: getGetUnsettledBookingsV2QueryKey(locationId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetBookingDetailDtoQueryKey(locationId, reference),
        });
      },
    },
  });
};
